import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { OrdemServico, PaginatedResponse } from '../types'

export interface OrdemServicoCreateInput {
  pmoc: string
  equipamento?: string | null
  plano?: string | null
  tecnico_responsavel?: string | null
  titulo?: string
  tipo_os: string
  prioridade?: 'normal' | 'alta' | 'urgente' | 'emergencial'
  data_prevista: string
  descricao?: string
}

export function useOsAprovacoesPendentes(params?: { tipo_os?: string; site?: string }) {
  return useQuery({
    queryKey: ['ordens-servico', 'aprovacoes-pendentes', params],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<OrdemServico>>(
        '/ordens-servico/aprovacoes-pendentes/',
        { params },
      )
      return r.data
    },
    staleTime: 1000 * 30,
  })
}

export function useOsList(params?: Record<string, string>) {
  return useQuery({
    queryKey: ['ordens-servico', params],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<OrdemServico>>('/ordens-servico/', { params })
      return r.data
    },
  })
}

export function useOsDetalhe(id: string) {
  return useQuery({
    queryKey: ['ordens-servico', id],
    queryFn: async () => {
      const r = await api.get<OrdemServico>(`/ordens-servico/${id}/`)
      return r.data
    },
    enabled: !!id,
  })
}

export function useOsCreate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: OrdemServicoCreateInput) => {
      // Limpa campos vazios para o serializer não rejeitar
      const payload: Record<string, unknown> = { ...data }
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' || payload[k] === null) delete payload[k]
      })
      const r = await api.post<OrdemServico>('/ordens-servico/', payload)
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ordens-servico'] }),
  })
}

export function useOsTransicao(id: string) {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: ['ordens-servico'] })

  const atribuir = useMutation({
    mutationFn: (tecnico_responsavel?: string) =>
      api.post(
        `/ordens-servico/${id}/atribuir/`,
        tecnico_responsavel ? { tecnico_responsavel } : {},
      ),
    onSuccess: invalidate,
  })

  const iniciar = useMutation({
    mutationFn: () => api.post(`/ordens-servico/${id}/iniciar/`),
    onSuccess: invalidate,
  })

  const concluir = useMutation({
    mutationFn: () => api.post(`/ordens-servico/${id}/concluir/`),
    onSuccess: invalidate,
  })

  const aprovar = useMutation({
    mutationFn: () => api.post(`/ordens-servico/${id}/aprovar/`),
    onSuccess: invalidate,
  })

  const cancelar = useMutation({
    mutationFn: (motivo_cancelamento: string) =>
      api.post(`/ordens-servico/${id}/cancelar/`, { motivo_cancelamento }),
    onSuccess: invalidate,
  })

  return { atribuir, iniciar, concluir, aprovar, cancelar }
}
